import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import type { WidgetChartType } from '@/dashboard/shared/types/widgets.types';
import type { AlertTargetItem } from '@/api-client/alert/model/alert-target-item';
import type { AlertDataItem } from '@/api-client/alert/model/alert-data-item';
import { WidgetUUID } from '@/common/utils';

export type ConvertAlertValue = Omit<AlertDataItem, 'dataIds'>;

export const EXCEPT_CHART_TYPE_BY_ALERT_HIGHLIGHT_API: WidgetChartType[] = [
  'VERTICAL_FILTER',
  'HORIZONTAL_FILTER',
  'MEMO',
  'IMAGE',
  'TEXT',
  'GROUP',
  'TAB_GROUP',
  'TOPOLOGY',
  'CLUSTER',
  'EQUALIZER',
  'SERVER',
  'STATUS',
];

export const getAlertKey = (targetId: string, dataId: string) => {
  return `${targetId}--${dataId}`;
};

export const useDashboardWidgetsAlertStore = defineStore('dashboard-widgets-alert', () => {
  const widgetsAlertList = shallowRef<Map<string, ConvertAlertValue[]> | undefined>(undefined);

  const setWidgetsAlertList = (data: AlertTargetItem[]) => {
    if (data.length === 0) {
      widgetsAlertList.value = undefined;
      return;
    }

    const alertValues = new Map<string, ConvertAlertValue[]>();

    data.forEach((item) => {
      const { alerts, targetId } = item;

      // targetId가 없거나 alerts가 없으면 건너뛰기
      if (!targetId || !alerts?.length) return;

      alerts.forEach((alert) => {
        if (!alert.dataIds?.length) {
          return;
        }

        const { dataIds, ...rest } = alert;

        if (dataIds?.length) {
          dataIds.forEach((dataId) => {
            if (!dataId) {
              return;
            }

            const key = getAlertKey(targetId, dataId);
            const alertValue = alertValues.get(key);
            if (alertValue) {
              alertValue.push(rest);
            } else {
              alertValues.set(key, [rest]);
            }
          });
        }
      });
    });

    widgetsAlertList.value = alertValues;
  };

  // 클릭한 위젯 정보 관리
  const clickedAlertWidgetInfo = ref<{
    widgetId?: WidgetUUID;
    widgetName?: string;
    fieldNames?: string[];
  }>({});

  const setClickedAlertWidgetInfo = (widgetInfo: {
    widgetId: WidgetUUID;
    widgetName: string;
    fieldNames?: string[];
  }) => {
    if (clickedAlertWidgetInfo.value.widgetId === widgetInfo.widgetId) {
      return;
    }

    clickedAlertWidgetInfo.value = widgetInfo;
  };

  const resetClickedAlertWidgetInfo = () => {
    clickedAlertWidgetInfo.value = {};
  };

  return {
    widgetsAlertList,
    setWidgetsAlertList,

    clickedAlertWidgetInfo,
    setClickedAlertWidgetInfo,
    resetClickedAlertWidgetInfo,
  };
});
